import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Alert, 
  Progress,
  Spinner 
} from 'reactstrap';
import { AccountClient, UsageResponseDto } from '../api/api-client';

export const UsageDashboard: React.FC = () => {
  const [usage, setUsage] = useState<UsageResponseDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      const client = new AccountClient();
      const usageData = await client.getUsage();
      
      setUsage(usageData);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading usage data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={10} lg={8} className="mx-auto">
          <h2 className="mb-4">Usage & Billing</h2>
          
          {usage && (
            <Row>
              <Col md={6} className="mb-4">
                <Card>
                  <CardHeader>
                    <h5 className="mb-0">Current Month Usage</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Usage:</span>
                        <strong>{formatCurrency(usage.currentMonthUsage ?? 0)}</strong>
                      </div>
                      {(usage.monthlyUsageLimit ?? 0) > 0 && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>Limit:</span>
                            <strong>{formatCurrency(usage.monthlyUsageLimit ?? 0)}</strong>
                          </div>
                          <Progress 
                            value={usage.usagePercentage ?? 0} 
                            color={usage.isLimitExceeded ? 'danger' : (usage.usagePercentage ?? 0) > 80 ? 'warning' : 'success'}
                            className="mb-2"
                          />
                          <small className="text-muted">
                            {(usage.usagePercentage ?? 0).toFixed(1)}% of monthly limit
                          </small>
                        </>
                      )}
                      {(usage.monthlyUsageLimit ?? 0) === 0 && (
                        <div className="text-success">
                          <i className="fas fa-infinity me-1"></i>
                          Unlimited usage
                        </div>
                      )}
                    </div>
                    
                    {usage.isLimitExceeded && (
                      <Alert color="danger" className="mb-0">
                        <strong>Usage limit exceeded!</strong> Additional usage may be restricted.
                      </Alert>
                    )}
                  </CardBody>
                </Card>
              </Col>
              
              <Col md={6} className="mb-4">
                <Card>
                  <CardHeader>
                    <h5 className="mb-0">Billing Information</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Reset Date:</span>
                        <strong>{usage.usageResetDate ? formatDate(usage.usageResetDate) : 'N/A'}</strong>
                      </div>
                    </div>
                    
                    <Alert color="info" className="mb-0">
                      <h6>Billing Features Coming Soon</h6>
                      <ul className="mb-0 small">
                        <li>Detailed usage history</li>
                        <li>Usage by AI model</li>
                        <li>Export usage reports</li>
                        <li>Set usage alerts</li>
                        <li>Manage payment methods</li>
                      </ul>
                    </Alert>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          
          {!usage && (
            <Alert color="warning">
              Unable to load usage data. Please try refreshing the page.
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};